/**
 * UnderwaterOverlay — the SUBMERGED-CAMERA screen effect (the games-standard
 * underwater recipe: Ilett's "Something Fishy"/"Sinking Feeling", Cyanilux's
 * water breakdown):
 *
 *  - depth-graded ABSORPTION veil (red dies first; the view converges to
 *    the deep-water color as the camera sinks),
 *  - near-surface CAUSTIC shimmer (Voronoi bands that fade with depth),
 *  - a soft VIGNETTE closing in toward the deep color.
 *
 * Implemented as a fullscreen CLIP-SPACE triangle (vertexNode returns the
 * clip vec4 directly — the r182 contract) BLENDED over the live render —
 * deliberately NOT a scene-copy re-shade: ViewportTextureNode copies once
 * per frame at node-update time, and inside a post pipeline (HDR target +
 * shadow passes) that copy can be the CLEARED target or a stale frame — the
 * submerged view then became a frozen sky-colored wash instead of the world
 * (2026-07-30). A transparent veil grades whatever is really on screen, on
 * every backend, per-view in XR. Draw it LAST (renderOrder 9999, no depth).
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from '../../render/post/mrtOverlay.js';
import {
  Fn,
  clamp,
  color,
  dot,
  exp,
  float,
  mix,
  mx_worley_noise_float,
  positionLocal,
  screenUV,
  smoothstep,
  uniform,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';

export interface UnderwaterOverlayConfig {
  /** Deep-water color the view converges to. Default 0x03212e. */
  deepColor?: THREE.ColorRepresentation;
  /** Shallow scatter tint multiplied into the near view. Default 0x2e8b8b. */
  shallowColor?: THREE.ColorRepresentation;
  /** Beer–Lambert extinction per channel (1/m). Default water-ish r-first. */
  absorption?: { r: number; g: number; b: number };
  /** Screen-UV distortion amplitude. Default 0.011. */
  distortionStrength?: number;
  /** Distortion band count across the screen. Default 6. */
  distortionScale?: number;
  /** Wobble speed multiplier. Default 1. */
  speed?: number;
  /** Caustic shimmer strength near the surface. Default 0.14. */
  causticStrength?: number;
}

export class UnderwaterOverlay {
  /** Fullscreen clip-space triangle; add to the scene and toggle visible. */
  readonly mesh: THREE.Mesh;
  /** Shared water clock (seconds). */
  readonly uTime = uniform(0);
  /** Camera depth below the water surface (m) — drives the grading. */
  readonly uDepth = uniform(0);
  /** Master fade 0..1 (lets callers ease the effect in over a few frames). */
  readonly uIntensity = uniform(1);

  constructor(config: UnderwaterOverlayConfig = {}) {
    const deep = new THREE.Color(config.deepColor ?? 0x03212e);
    const shallow = new THREE.Color(config.shallowColor ?? 0x2e8b8b);
    const absorption = config.absorption ?? { r: 0.35, g: 0.12, b: 0.08 };
    const speed = config.speed ?? 1;
    const causticStrength = config.causticStrength ?? 0.14;

    // One triangle covering clip space — no camera parenting, works per-eye.
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3),
    );
    // Dummy +Z normals — the overlay is unlit, but TSL's normal accessor
    // warns (then zero-fills) on any geometry without the attribute.
    geometry.setAttribute(
      'normal',
      new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 0, 1, 0, 0, 1]), 3),
    );

    const material = new THREE.MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthTest = false;
    material.depthWrite = false;
    material.fog = false;
    markMrtOverlay(material); // a screen tint, not a surface

    const uTime = this.uTime;
    const uDepth = this.uDepth;
    const uIntensity = this.uIntensity;

    material.vertexNode = Fn(() => vec4(positionLocal.x, positionLocal.y, 0, 1))();

    material.colorNode = Fn(() => {
      const t = uTime.mul(speed);
      const suv = screenUV;

      // ── Depth-graded absorption veil: Beer–Lambert over the water column
      // between the camera and the world — red dies first, the veil deepens
      // and blues as the camera sinks. The LIVE scene stays visible through
      // it (alpha), never replaced by a copy.
      const column = float(2.0).add(clamp(uDepth, 0, 60).mul(0.5));
      const transmit = exp(
        vec3(-absorption.r, -absorption.g, -absorption.b).mul(column),
      );
      const scatter = mix(color(shallow), color(deep), smoothstep(0, 30, uDepth));

      // ── Caustic shimmer near the surface (fades out by ~12 m down).
      const causticUv = vec3(suv.x.mul(9), suv.y.mul(9).add(t.mul(0.22)), t.mul(0.31));
      const caustic = mx_worley_noise_float(causticUv);
      const causticGain = float(causticStrength)
        .mul(float(1).sub(smoothstep(1.5, 12.0, uDepth)));
      let veil = scatter.add(
        vec3(0.7, 0.9, 1.0).mul(caustic.mul(caustic).mul(causticGain)),
      ) as unknown as ReturnType<typeof vec3>;

      // ── Vignette: edges close toward the deep color (pressure feel).
      const off = suv.sub(vec2(0.5, 0.5));
      const edge = smoothstep(0.12, 0.62, dot(off, off));
      veil = mix(veil, color(deep), edge.mul(0.6)) as unknown as typeof veil;

      // Coverage: how much the veil obscures — grows with depth + vignette,
      // capped so the world never fully disappears behind it.
      const cover = clamp(
        float(1).sub(transmit.g).mul(0.72).add(edge.mul(0.18)),
        0.08,
        0.85,
      );
      return vec4(veil, cover.mul(uIntensity));
    })();

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.name = 'UnderwaterOverlay';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 9999;
    this.mesh.visible = false;
  }

  /** Per-frame update while submerged. */
  update(time: number, depthBelowSurface: number): void {
    this.uTime.value = time;
    this.uDepth.value = Math.max(0, depthBelowSurface);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
